import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Grid,
  GridItem,
  Heading,
  Spinner,
  Text,
} from '@chakra-ui/react';
import Card from '../../../components/Card/Card';
import { useStore } from '../../../utils/useStore';
import { ServiceViewProps } from './interfaces';
import useServicesViewController from './services.controller';

const SvgFromUrl = ({
  url,
  size = '2.6rem',
  title,
  color,
  dimmed,
}: {
  url: string;
  size?: string | number;
  title?: string;
  color?: string;
  dimmed?: boolean;
}) => {
  const [svg, setSvg] = useState<string>('');
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const res = await fetch(url, { mode: 'cors' });
        const text = await res.text();
        if (!cancelled) setSvg(text);
      } catch (e) {
        if (!cancelled) setSvg('');
      }
    };
    if (url) run();
    return () => {
      cancelled = true;
    };
  }, [url]);
  if (!svg) return <Box boxSize={size} />;
  return (
    <Box
      boxSize={size}
      title={title}
      color={color}
      sx={{
        svg: {
          width: '100%',
          height: '100%',
          display: 'block',
          filter: dimmed ? 'brightness(0.7)' : 'none',
        },
        'svg *': {
          fill: 'currentColor',
          stroke: 'currentColor',
        },
      }}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
};

const ServicesView: React.FC<ServiceViewProps> = ({
  useController = useServicesViewController,
}) => {
  const controller = useController();
  const { activePoint } = useStore();

  const initialIds = activePoint?.services?.map(s => s._id) ?? [];
  const [selectedIds, setSelectedIds] = React.useState<string[]>(initialIds);

  React.useEffect(() => {
    setSelectedIds(activePoint?.services?.map(s => s._id) ?? []);
  }, [activePoint?._id]); // cuando cambia el point activo

  const hasChanges =
    selectedIds.length !== initialIds.length ||
    selectedIds.some(id => !initialIds.includes(id));

  return !activePoint ? (
    <Center my={8}>
      <Spinner size="xl" />
    </Center>
  ) : (
    <Card mt={'8%'}>
      <Heading fontWeight={'medium'} p={2} fontSize={'3xl'}>
        Servicios
      </Heading>
      <Divider />
      <Box p={7}>
        <Grid templateColumns={'repeat(3, 1fr)'} gap={10}>
          <GridItem colSpan={3} px={'3%'}>
            <Text>
              Indica a los visitantes de tu tienda que pueden encontrar y cuales
              son los servicios que se ofrecen, para aumentar la cantidad de
              potenciales clientes.
            </Text>
          </GridItem>
          {controller.isLoadingServices ? (
            <GridItem colSpan={3}>
              <Center my={8}>
                <Spinner size="xl" />
              </Center>
            </GridItem>
          ) : (
            controller.services.map(service => {
              const isServiceActive = selectedIds.includes(service._id);

              return (
                <GridItem
                  key={'service-item-' + service._id}
                  colSpan={{
                    sm: 3,
                    md: 1,
                  }}
                  h="12vh">
                  <Card
                    onClick={() => {
                      setSelectedIds(prev =>
                        prev.includes(service._id)
                          ? prev.filter(id => id !== service._id)
                          : [...prev, service._id]
                      );
                    }}
                    bgColor={isServiceActive ? 'brand.50' : '#595966'}
                    borderRadius="12"
                    h="100%"
                    maxW={{ sm: '100%', md: '80%' }}
                    marginX={'auto'}
                    _hover={{
                      cursor: 'pointer',
                      bgColor: isServiceActive ? 'brand.100' : '#474752',
                    }}
                    px={3}
                    py={1}>
                    <Flex
                      gap="5"
                      alignItems={'center'}
                      justifyContent="space-between"
                      my={'auto'}>
                      <Text
                        color={isServiceActive ? 'black' : '#C4C4C4'}
                        fontSize={'1rem'}
                        noOfLines={2}>
                        {service.label}
                      </Text>
                      {service.webIconUrl ? (
                        <SvgFromUrl
                          url={service.webIconUrl}
                          title={service.label}
                          size="2.6rem"
                          color={isServiceActive ? '#000' : '#C4C4C4'}
                          dimmed={!isServiceActive}
                        />
                      ) : null}
                    </Flex>
                  </Card>
                </GridItem>
              );
            })
          )}
        </Grid>

        <Flex pt={10} w="100%" justifyContent="flex-end" gap={3}>
          <Button
            textColor={'black'}
            isDisabled={!hasChanges || controller.isSubmitting}
            backgroundColor={
              controller.isSubmitting ? 'rgba(255, 240, 255, 0.2)' : '#FFC700'
            }
            isLoading={controller.isSubmitting}
            onClick={() => controller.saveServices(selectedIds)}>
            Guardar cambios
          </Button>
          <Button
            variant="ghost"
            isDisabled={controller.isSubmitting}
            onClick={() => setSelectedIds(initialIds)}
            backgroundColor={'rgba(255, 240, 255, 0.2)'}>
            Cancelar
          </Button>
        </Flex>
      </Box>
    </Card>
  );
};

export default ServicesView;
